import { memo } from "react";

import { Navigate, Outlet } from "react-router-dom";

import { ROUTES } from "@/constants";
import { useAppSelector } from "@/store/hooks";
import type { UserRole } from "@/types/domain";
import { useEventsFeature } from "@/features/events";
import { useRequestToJoin } from "@/graphql/hooks/useEvents";

interface RoleGuardProps {
  allowedRoles: UserRole[];
}

/**
 * Restricts a subtree of routes to users with specific roles.
 * Must be nested inside AuthGuard (assumes user is authenticated).
 *
 * NOTE: This guard now consults the server-side joinState (getRequestToJoin)
 * to avoid allowing admin routes based solely on a cached currentEvent.canManageRequests
 * flag which can be stale or computed differently. This increases security by
 * preferring the server-authoritative joinState when available.
 */
const RoleGuard = memo(function RoleGuard({ allowedRoles }: RoleGuardProps) {
  const companies = useAppSelector((s) => s.company.companies);
  const activeCompany = useAppSelector((s) => s.company.activeCompany);
  const isRestoringSession = useAppSelector((s) => s.user.isRestoringSession);
  const { currentEvent } = useEventsFeature();

  const matchedCompany = companies.find(
    (company) => company?.companyId === activeCompany?.id,
  );

  // Consult server-authoritative join state for the current event when possible
  const { joinState, loading: joinLoading } = useRequestToJoin(
    currentEvent?.id ?? "",
    Boolean(currentEvent?.id),
  );

  if (isRestoringSession) {
    // While the session is being restored, don't render anything
    return (
      <div className="flex h-screen items-center justify-center py-20">
        <div
          className="h-8 w-8 animate-spin rounded-full border-4 border-neutral-200 border-t-[var(--sidebar-bg)] text-center"
          aria-label="Loading..."
        />
      </div>
    );
  }

  // On a fresh load the event is fetched asynchronously by AppShell. Until it
  // arrives there is nothing to authorise against, so wait rather than redirect.
  if (!currentEvent) {
    return (
      <div className="flex h-screen items-center justify-center py-20">
        <div
          className="h-8 w-8 animate-spin rounded-full border-4 border-neutral-200 border-t-[var(--sidebar-bg)] text-center"
          aria-label="Loading event..."
        />
      </div>
    );
  }

  // If the joinState is still loading, show a loader rather than risk
  // rendering the guarded subtree based on potentially stale client-only flags.
  if (joinLoading) {
    return (
      <div className="flex h-screen items-center justify-center py-20">
        <div
          className="h-8 w-8 animate-spin rounded-full border-4 border-neutral-200 border-t-[var(--sidebar-bg)] text-center"
          aria-label="Loading permissions..."
        />
      </div>
    );
  }

  // Determine permission using server joinState when available.
  // Fallback to company-role checks if joinState is not present for this event.
  const serverAllows = joinState?.canManageRequests === true;
  const companyAllows =
    matchedCompany?.role &&
    allowedRoles.includes(matchedCompany.role as UserRole) &&
    currentEvent?.organiser?.company?.id === activeCompany?.id;

  if (!serverAllows && !companyAllows) {
    // Redirect to dashboard — user is authenticated but lacks permission
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  return <Outlet />;
});

export default RoleGuard;
